import { Dispatch, SetStateAction, useCallback, useRef, useState } from "react";

/**
 * A custom React hook that wraps `useState` and executes a side effect action whenever the state is updated.
 *
 * This hook helps avoid unnecessary `useEffect` hooks by handling the side effect directly in the state setter,
 * following the pattern described in React's official documentation:
 * https://react.dev/learn/you-might-not-need-an-effect
 *
 * Instead of:
 * ```tsx
 * const [count, setCount] = useState(0);
 * useEffect(() => {
 *   onCountChange(count);
 * }, [count, onCountChange]);
 * ```
 *
 * Use this hook to handle the side effect directly when the state changes:
 * ```tsx
 * const [count, setCount] = useStateWithSetAction(0, onCountChange);
 * ```
 *
 * The returned `setState` function is **always stable** (same reference across renders), even if `setAction`
 * is defined inline. This prevents unnecessary re-renders in child components that depend on `setState`.
 *
 * @template T - The type of the state value
 *
 * @param initialValue - The initial state value or a function that returns the initial value
 * @param setAction - A callback function that will be invoked with the new state value
 *                                          whenever the state is updated. This is called with the computed
 *                                          new value before the state update is applied.
 *
 * @returns A tuple containing:
 *   - The current state value
 *   - A setState function that behaves like the standard React setState but also calls setAction
 *
 * @example
 * ```tsx
 * // Basic usage with direct value updates
 * const [count, setCount] = useStateWithSetAction(
 *   0,
 *   (newCount) => console.log('Count updated to:', newCount)
 * );
 *
 * setCount(5); // Logs: "Count updated to: 5"
 * ```
 *
 * @example
 * ```tsx
 * // Usage with updater function
 * const [items, setItems] = useStateWithSetAction(
 *   [],
 *   (newItems) => localStorage.setItem('items', JSON.stringify(newItems))
 * );
 *
 * setItems(prev => [...prev, newItem]); // Persists to localStorage
 * ```
 *
 * @example
 * ```tsx
 * // Usage with inline callback (setAction can change without affecting setState stability)
 * const [formData, setFormData] = useStateWithSetAction(
 *   initialData,
 *   (newData) => onFormChange?.(newData) // Inline function is fine!
 * );
 * ```
 */
export const useStateWithSetAction = <T>(
  initialValue: T | (() => T),
  setAction: (newVal: T) => void
): [T, Dispatch<SetStateAction<T>>] => {
  const [state, _setState] = useState<T>(initialValue);

  // Store setAction in a ref to ensure setState remains stable even when setAction changes
  const setActionRef = useRef(setAction);

  // Update ref during render to always have the latest setAction
  setActionRef.current = setAction;

  const setState = useCallback<typeof _setState>(
    (newVal) => {
      _setState((prev) => {
        let computedNewVal = prev;
        if (newVal instanceof Function) {
          computedNewVal = newVal(prev);
        } else {
          computedNewVal = newVal;
        }
        // Use the ref to call the latest setAction
        setActionRef.current(computedNewVal);
        return computedNewVal;
      });
    },
    [] // Empty dependencies - setState is always stable!
  );

  return [state, setState];
};
